import React, { useEffect, useMemo, useState } from "react";
import { useMaxHeightTransition } from "components/utility/useMaxHeightTransition";
import Icon from "resources/icons/Icon";
import { ScreenState } from "App";
import { useCMTagList } from "api/api";
import TickValue from "components/basic/TickValue";
import moment from "moment";
import HoverTooltip from "components/basic/HoverTooltip";
import LoadingIcon from "components/basic/LoadingIcon/LoadingIcon";
import { Power_Status } from "model/Datatypes";

interface Props {
  screenState: ScreenState;
  StatusTags: Power_Status;
}

export const Status = ({ screenState, StatusTags }: Props) => {
  const { productionStatus, currentOperations, currentActive, loading } =
    useProductionStatus(StatusTags);

  const powerStatus = usePowerStatus(StatusTags); // % running compressors
  const { open, setOpen } = useMaxHeightTransition("7rem", "14rem");

  useEffect(() => {
    if (screenState === "Frontpage" || screenState === "Transition-To-FP") setOpen(false);
    else setOpen(true);
  }, [screenState, setOpen]);

  const cardColor = currentOperations === "Heating" ? "#FF5F45" : "#4EA6FF";

  return (
    <div
      style={{ backgroundColor: cardColor }}
      className={`rounded-md w-full overflow-hidden border text-white shadow-glow-green flex flex-col relative h-full`}
    >
      {loading && (
        <div
          className="absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-75 text-white"
          style={{ fontSize: "4rem", backgroundColor: cardColor }}
        >
          <LoadingIcon />
        </div>
      )}
      {open && (
        <div className="px-4 pt-4 2xl:pt-8 2xl:px-8">
          <div className={`font-normal text-sm 2xl:text-4xl`}>Current status</div>
          <div
            className={`border-b border-white font-medium
                text-xl 2xl:text-6xl 2xl:mt-6 mt-1 pb-2 flex`}
          >
            {currentOperations}
            {/* {currentActive.hot_water && (
              <div className="text-sm mt-1 font-light">and producing hot water</div>
            )} */}
          </div>
        </div>
      )}

      <div className="flex-grow relative">
        <div className="h-full w-full absolute px-4 2xl:px-8 transform top-8 2xl:top-40">
          <div className="flex flex-row-reverse mb-6 2xl:mb-12">
            <div className="h-10 w-full"></div>
            <HoverTooltip text={`Heating: ${productionStatus.heating.toFixed(1)}kW`}>
              <div
                className={`${tw.icon} mr-4 h-10 w-10 2xl:h-28 2xl:w-28`}
                style={{
                  opacity: currentActive.heating ? 1 : 0.25,
                }}
              >
                <Icon type="heating" className="w-full h-5 2xl:h-10 " />
              </div>
            </HoverTooltip>
            <HoverTooltip text={`Hot water: ${productionStatus.hotWater.toFixed(1)}kW`}>
              <div
                className={`${tw.icon} mr-4 h-10 w-10 2xl:h-28 2xl:w-28`}
                style={{
                  opacity: currentActive.hot_water ? 1 : 0.25,
                }}
              >
                <Icon type="hot-water" className="w-full h-5 2xl:h-12" />
              </div>
            </HoverTooltip>
            <HoverTooltip text={`Cooling: ${productionStatus.cooling.toFixed(1)}kW`}>
              <div
                className={`${tw.icon} mr-4 h-10 w-10 2xl:h-28 2xl:w-28`}
                style={{
                  opacity: currentActive.cooling ? 1 : 0.25,
                }}
              >
                <Icon type="cooling" className="w-full h-7 2xl:h-16" />
              </div>
            </HoverTooltip>
          </div>
          <div className="h-12 2xl:h-28 w-full shadow-md relative overflow-hidden">
            <div
              className="bg-white h-full text-xs 2xl:text-3xl relative"
              style={{
                width: `${powerStatus}%`,
                transition: "width 0.5s ease-in-out",
              }}
            >
              <div
                className={`absolute flex items-center justify-center h-6 w-8 top-1/2 ${
                  powerStatus > 15 ? "text-cyan right-1/2" : "text-white right-0"
                }`}
                style={{
                  marginRight: powerStatus > 15 ? "-1rem" : "-2rem",
                  marginTop: "-0.75rem",
                }}
              >
                <TickValue value={powerStatus} />%
              </div>
            </div>
            <div className="absolute w-full h-full top-0 left-0 bg-gray-100 opacity-25"></div>
          </div>
        </div>
      </div>
      {open && (
        <div className="px-4 2xl:px-8">
          <div className="border-t border-white flex items-center justify-between h-12 2xl:h-24 text-xs 2xl:text-2xl">
            <div className="">Real time production and load</div>
          </div>
        </div>
      )}
    </div>
  );
};

const tw = {
  icon: "bg-white w-10 h-10 p-1 rounded-sm shadow-md mr-2 flex items-center justify-center",
};

export default Status;

export const usePowerStatus = (power: Power_Status) => {
  const timeFrom = useMemo(() => moment().subtract(30, "minutes").valueOf(), []);
  const timeTo = useMemo(() => moment().valueOf(), []);

  const tagsToLoad = useMemo(
    () => (power.powerBy === "compressors" ? power.tags : [power.tag]),
    [power]
  );

  const { cmData: cmPowerstatusData } = useCMTagList(
    tagsToLoad,
    timeFrom,
    timeTo,
    "MINUTES",
    true
  );

  const [powerStatus, setPowerStatus] = useState(0);

  useEffect(() => {
    if (power.powerBy === "electricity" && cmPowerstatusData) {
      const currentPowerdata = cmPowerstatusData[0];
      if (currentPowerdata) {
        const latestReading =
          currentPowerdata.points[currentPowerdata.points.length - 1]?.value;
        const percentOfMax =
          latestReading && (Math.abs(latestReading) / power.maxCapacity) * 100;
        setPowerStatus(percentOfMax || 0);
      }
      // const currentProduction
    } else if (power.powerBy === "compressors" && cmPowerstatusData) {
      //add 10% for each compressor that is active at latest reading
      const procentRunning = cmPowerstatusData.reduce((prev, cur) => {
        if (cur.points.length > 0 && cur.points[cur.points.length - 1].value > 0)
          return prev + 10;
        return prev;
      }, 0);
      setPowerStatus(procentRunning);
    } else {
      setPowerStatus(0);
    }
  }, [cmPowerstatusData, power]);

  return powerStatus;
};

const useProductionStatus = (StatusTags: Power_Status) => {
  const timeFrom = useMemo(() => moment().subtract(30, "minutes").valueOf(), []);
  const timeTo = useMemo(() => moment().valueOf(), []);

  const productionTags = useMemo(() => {
    return [StatusTags.heating, StatusTags.cooling, StatusTags.hot_water];
  }, [StatusTags]);

  const { cmData: cmProductionData, loading } = useCMTagList(
    productionTags,
    timeFrom,
    timeTo,
    "MINUTES",
    true
  );
  const [productionStatus, setproductionStatus] = useState({
    heating: 0,
    cooling: 0,
    hotWater: 0,
  });

  const [currentOperations, setCurrentOperations] = useState<"Heating" | "Cooling">("Heating");
  const [currentActive, setcurrentActive] = useState({
    cooling: false,
    heating: false,
    hot_water: false,
  });

  useEffect(() => {
    const heatingVals = cmProductionData?.find(
      (res) => res.name === StatusTags.heating
    )?.points;
    const heating = heatingVals ? heatingVals[heatingVals.length - 1].value : 0;
    const hotWaterVals = cmProductionData?.find(
      (res) => res.name === StatusTags.hot_water
    )?.points;
    const hotWater = hotWaterVals ? hotWaterVals[hotWaterVals.length - 1].value : 0;
    const coolingVals = cmProductionData?.find(
      (res) => res.name === StatusTags.cooling
    )?.points;
    const cooling = coolingVals ? coolingVals[coolingVals.length - 1].value : 0;
    setproductionStatus({
      heating: Math.abs(heating),
      cooling: Math.abs(cooling),
      hotWater: Math.abs(hotWater),
    });
  }, [cmProductionData, StatusTags]);

  useEffect(() => {
    setcurrentActive({
      heating: productionStatus.heating > StatusTags.lowerThreshhold,
      cooling: productionStatus.cooling > StatusTags.lowerThreshhold,
      hot_water: productionStatus.hotWater > StatusTags.lowerThreshhold,
    });
    setCurrentOperations(
      productionStatus.cooling > productionStatus.heating ? "Cooling" : "Heating"
    );
  }, [productionStatus, StatusTags.lowerThreshhold]);

  return { productionStatus, currentOperations, loading, currentActive };
};
